import React, { useState } from 'react';
import { Button, Card, Input, Modal, Space, Table, TablePaginationConfig, Tabs } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined, InfoCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';

import { ApiError, Firmware } from '@services/types';
import api from '@services/api';
import { toast } from 'react-toastify';
import UnusedFirmware from '@layouts/firmwares/Firmwares/UnusedFirmwares';
import { AppContext } from '@layouts/AppRouter';
import { Navigation } from '@constants/Navigation';
import { useHistory } from 'react-router-dom';

const { Search } = Input;
const { Column } = Table;
const { TabPane } = Tabs;

const Firmwares = () => {
  const [firmwares, setFirmwares] = useState<Firmware[]>([]);
  const [searchResult, setSearchResult] = React.useState('');
  const { deviceFilter, setDeviceFilter } = React.useContext(AppContext);
  const history = useHistory();
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    total: firmwares.length,
  });

  React.useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult.length]);

  const onSearch = React.useCallback(
    (newPagination = pagination) => {
      if (deviceFilter?.length) {
        api.firmwares.getFirmwaresDeviceId({ deviceTypeId: deviceFilter }).then((newFirmwares) => {
          setFirmwares(newFirmwares.elements);
          setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
            showSizeChanger: true,
            total: newFirmwares.totalElementCount || newFirmwares.elements.length,
          });
          setDeviceFilter('');
        });
      } else {
        if (searchResult.length >= 0) {
          api.firmwares
            .getFirmwares({
              filter: searchResult,
              limit: newPagination.pageSize,
              offset: newPagination.current - 1,
            })
            .then((newFirmwares) => {
              setFirmwares(newFirmwares.elements);
              setPagination({
                current: newPagination.current,
                pageSize: newPagination.pageSize,
                showSizeChanger: true,
                total: newFirmwares.totalElementCount || newFirmwares.elements.length,
              });
            });
        }
      }
    },
    [pagination, searchResult, searchResult.length],
  );

  const handleTableChange = React.useCallback(
    (newPagination: TablePaginationConfig) => {
      setPagination(newPagination);
      onSearch(newPagination);
    },
    [onSearch],
  );

  const showDeleteConfirm = (firmware: Firmware) => {
    Modal.confirm({
      title: 'Are you sure delete this firmware?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.firmwares.deleteFirmware(firmware.id);
          setFirmwares(firmwares.filter(({ id }) => id !== firmware.id));
          toast.success('Firmware was deleted successfully');
        } catch (response) {
          if (response instanceof ApiError) {
            toast.error(`${response.error.details || response.error.propertyErrors?.name}`);
          } else {
            toast.error('Something went wrong');
          }
          console.log('Firmware ~ handleSubmit ~ error', response);
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const firmCount = (pagination.pageSize || 0) * (pagination.current || 0);
  const firmItem = (firmCount || 0) - ((pagination.pageSize || 0) - 1);

  return (
    <Tabs type="card" size="large" defaultActiveKey={'firmwares' || 'unused'}>
      <TabPane tab="Firmwares" key="firmwares">
        <Card
          title={`My firmwares: ${firmItem}-${firmCount} / ${pagination.total}`}
          extra={
            <Space>
              <Search
                onChange={(e) => {
                  setSearchResult(e.target.value);
                }}
                placeholder="Search"
                allowClear
                enterButton
                size="middle"
                onSearch={() => onSearch()}
              />
              <Button type="primary" onClick={() => history.push(Navigation.CreateFirmware)}>
                Add
              </Button>
            </Space>
          }
          bodyStyle={{ padding: 0 }}
        >
          <Table
            dataSource={firmwares}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey={(record: Firmware) => record.id}
            scroll={{ x: 1200 }}
          >
            <Column
              title="Modification date"
              width="150px"
              align="center"
              render={(date: Firmware['modifiedAt']) => date?.toLocaleString()}
              dataIndex="modifiedAt"
            />
            <Column
              title="Compilation date"
              width="150px"
              align="center"
              render={(date: Firmware['date']) => date?.toLocaleString()}
              dataIndex="date"
            />
            <Column
              title="Device Type"
              align="center"
              render={(deviceTypes: Firmware['deviceTypes']) => {
                if (deviceTypes?.length === 0) {
                  return 'Unknown';
                } else if (deviceTypes?.length === 1) {
                  return deviceTypes[0];
                } else {
                  return 'Multiple device types';
                }
              }}
              dataIndex="deviceTypes"
              ellipsis
            />
            <Column title="Name" align="center" dataIndex="name" />
            <Column title="Type" align="center" dataIndex="type" />
            <Column title="Version" align="center" dataIndex="version" />
            <Column
              title="Actions"
              width="130px"
              key="action"
              align="center"
              fixed="right"
              render={(text, firmware: Firmware) => (
                <Space size="middle">
                  <InfoCircleOutlined
                    onClick={() => history.push(Navigation.FirmwaresInfo.replace(':id', firmware.id))}
                  />
                  <PlayCircleOutlined
                    onClick={() => history.push(Navigation.DeployFirmware.replace(':id', firmware.id))}
                  />
                  <DeleteOutlined onClick={() => showDeleteConfirm(firmware)} />
                </Space>
              )}
            />
          </Table>
        </Card>
      </TabPane>
      <TabPane tab="Unused Firmwares" key="unused">
        <UnusedFirmware />
      </TabPane>
    </Tabs>
  );
};

export default React.memo(Firmwares);
